<template>
  <AppContainer :is-loading justify-center>
    <EHeroContent :icon :variant :title description="">
      <template #action>
        <EButton
          v-if="showTryAgainBtn"
          icon="arrows-rotate"
          :icon-animation="isTryingAgain ? 'spin' : undefined"
          :label="t(useTranslationPath(callerPath, 'tryAgain'))"
          variant="secondary"
          :disabled="isTryingAgain"
          @click="tryAgain"
        />
        <EButton
          v-if="showKbArticleBtn"
          icon="circle-info"
          :label="t(useTranslationPath(callerPath, 'locationNotEnabledOrAvailable'))"
          variant="secondary"
          @click="goToKBArticle"
        />
      </template>
    </EHeroContent>

    <template #bottom>
      <div class="space-y-2">
        <EButton
          v-if="showContinueWithoutVerifyingLocationBtn"
          :label="t(useTranslationPath(callerPath, 'continueWithoutVerifying'))"
          variant="secondary"
          block
          @click="emits('continue', false, null)"
        />
        <EButton
          v-if="showEnterSiteAccessCodeBtn"
          :label="t(useTranslationPath(callerPath, 'enterSiteAccessCode'))"
          block
          @click="emits('accessCode')"
        />
        <EButton
          v-if="showActionBtn"
          :label="actionLabel"
          block
          @click="emits('continue', true, coordinates?.geometry?.coordinates)"
        />
      </div>
    </template>
  </AppContainer>
</template>

<script setup>
import AppContainer from '@/components/AppContainer.vue'
import { useTranslationPath } from '@/composables/useTranslationPath.js'
import { isLocationEnabled } from '@/composables/useVerificationMethod.js'
import { EngineerSessionLocationVerificationMethod } from '@/enums/EngineerSessionLocationVerificationMethod.js'
import { useAuthStore } from '@/stores/authenticated/AuthStore'
import { useSiteStore } from '@/stores/authenticated/SiteStore'
import { EButton, EHeroContent } from '@expansivefm/eds'
import { booleanPointInPolygon, booleanValid, point, polygon } from '@turf/turf'
import { isEmpty } from 'lodash'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  actionLabel: {
    type: String,
    required: true,
  },
  verificationMethod: {
    type: String,
    required: true,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
})

const emits = defineEmits(['continue', 'accessCode'])

const { t } = useI18n()

const authStore = useAuthStore()
const siteStore = useSiteStore()

const callerPath = 'components.verification.geoVerification'

const siteLocation = ref(null)
const locationStatus = ref('unknown')
const coordinates = ref(null)
const isTryingAgain = ref(false)

const kbArticleUrl = computed(() => {
  const kbUrl = import.meta.env.VITE_LOCATION_KB

  if (typeof kbUrl === 'string' && kbUrl.trim().length > 0) {
    return kbUrl
  }

  return null
})

const showKbArticleBtn = computed(
  () =>
    ['unavailable', 'not-enabled', 'not-available'].includes(locationStatus.value) &&
    kbArticleUrl.value !== null,
)

const showTryAgainBtn = computed(() =>
  ['unavailable', 'unknown', 'not-verified'].includes(locationStatus.value),
)

const showEnterSiteAccessCodeBtn = computed(() => {
  if (
    props.verificationMethod !==
    EngineerSessionLocationVerificationMethod.GEOLOCATION_AND_ACCESS_CODE
  ) {
    return false
  }

  return ['unavailable', 'unknown', 'not-verified', 'not-enabled', 'not-available'].includes(
    locationStatus.value,
  )
})

const showContinueWithoutVerifyingLocationBtn = computed(() => {
  return (
    props.verificationMethod !==
      EngineerSessionLocationVerificationMethod.GEOLOCATION_AND_ACCESS_CODE &&
    !isWithinFencing.value
  )
})

const showActionBtn = computed(() => {
  return isWithinFencing.value && locationStatus.value === 'verified'
})

const isWithinFencing = computed(() => {
  const validatedFences = siteStore.getSiteGeoFencing.filter((fence) => {
    try {
      return booleanValid(polygon(fence.geometry.coordinates))
    } catch (error) {
      return false
    }
  })

  if (
    isEmpty(siteStore.getSiteGeoFencing) ||
    coordinates.value === null ||
    isEmpty(validatedFences)
  ) {
    return false
  }

  return validatedFences
    .map((fence) => {
      return booleanPointInPolygon(coordinates.value, polygon(fence.geometry.coordinates))
    })
    .some((fence) => fence === true)
})

const icon = computed(() => {
  switch (locationStatus.value) {
    case 'verified':
      return 'location-check'
    case 'not-verified':
    case 'not-enabled':
    case 'not-available':
      return 'location-xmark'
    case 'unavailable':
      return 'location-dot-slash'
    case 'unknown':
    default:
      return 'location-question'
  }
})

const variant = computed(() => {
  switch (locationStatus.value) {
    case 'verified':
      return 'success'
    case 'not-verified':
    case 'not-enabled':
    case 'not-available':
      return 'danger'
    case 'unavailable':
      return 'warning'
    case 'unknown':
    default:
      return 'secondary'
  }
})

const title = computed(() => {
  switch (locationStatus.value) {
    case 'verified':
      return t(useTranslationPath(callerPath, 'verified.header'))
    case 'not-verified':
      return t(useTranslationPath(callerPath, 'notVerified.header'))
    case 'unavailable':
      return t(useTranslationPath(callerPath, 'unavailable.header'))
    case 'not-enabled':
      return t(useTranslationPath(callerPath, 'notEnabled.header'))
    case 'not-available':
      return t(useTranslationPath(callerPath, 'notAvailable.header'))
    case 'unknown':
    default:
      return t(useTranslationPath(callerPath, 'unknown.header'))
  }
})

onMounted(async () => {
  if (!isLocationEnabled(props.verificationMethod)) {
    emits('continue', false, null)
  } else {
    await siteStore.fetchSite(authStore.maintainableEntity.id).then(() => {
      if (!isEmpty(siteStore.site) && siteStore.site.longitude && siteStore.site.latitude) {
        siteLocation.value = point([siteStore.site.longitude, siteStore.site.latitude])
      }

      checkGeolocationPermission()
    })
  }
})

const checkGeolocationPermission = async () => {
  if ('permissions' in navigator) {
    await navigator.permissions
      .query({ name: 'geolocation' })
      .then((permissionStatus) => {
        if (permissionStatus.state === 'granted' || permissionStatus.state === 'prompt') {
          getCurrentLocation()
        } else if (permissionStatus.state === 'denied') {
          locationStatus.value = 'not-enabled'
        }

        permissionStatus.onchange = () => {
          if (permissionStatus.state === 'granted') {
            getCurrentLocation()
          } else if (permissionStatus.state === 'denied') {
            locationStatus.value = 'not-enabled'
          }
        }
      })
      .catch(() => {
        getCurrentLocation()
      })
  } else {
    getCurrentLocation()
  }
}

const getCurrentLocation = () => {
  if ('geolocation' in navigator) {
    navigator.geolocation.getCurrentPosition(
      (position) => {
        coordinates.value = point([position.coords.longitude, position.coords.latitude])

        if (!isEmpty(siteStore.getSiteGeoFencing)) {
          locationStatus.value = isWithinFencing.value ? 'verified' : 'not-verified'
        } else {
          locationStatus.value = 'not-verified'
        }
      },
      async () => {
        try {
          const permissionStatus = await navigator.permissions.query({ name: 'geolocation' })

          if (permissionStatus.state === 'denied') {
            locationStatus.value = 'not-enabled'
          } else {
            locationStatus.value = 'unavailable'
          }
        } catch {
          locationStatus.value = 'unavailable'
        }
      },
      {
        enableHighAccuracy: true,
      },
    )
  } else {
    locationStatus.value = 'not-available'
  }
}

const tryAgain = () => {
  isTryingAgain.value = true

  getCurrentLocation()

  setTimeout(() => (isTryingAgain.value = false), 4000)
}

const goToKBArticle = () => {
  window.open(kbArticleUrl.value, '_blank')
}
</script>
